import { useState } from "react";
import { Box, Button, Chip, IconButton, Link, Skeleton, Stack, Typography } from "@mui/material";
import { MRT_TableInstance } from "material-react-table";
import Iconify from "../../minimals/components/iconify";
import { getStatusColor } from "../calendar/CalendarUtils";
import { fCurrency } from "../utils/formatNumber";
import { fMonthDayYearSlashNotation } from "../utils/formatTime";
import ChecklistIcon from "./ChecklistIcon";
import { TMEOrderDetails } from "./types";

const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
const PAGE_SIZE = 30;

type Props = {
  table: MRT_TableInstance<TMEOrderDetails>;
  isLoading: boolean;
  /** Length of the real data. While loading with none, MRT substitutes 20 all-null
   *  placeholder rows (for its desktop skeleton cells), so the row model alone can't
   *  tell loading apart from a list of empty orders. */
  orderCount: number;
};

/**
 * The mobile rendering of the orders table. It reads rows from the same MRT
 * instance the desktop table uses — after filtering and sorting, before pagination —
 * so the role-based default filters and sort apply identically on both.
 */
export default function MobileOrderCards({ table, isLoading, orderCount }: Props) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const rows = table.getPrePaginationRowModel().rows;
  const visibleRows = rows.slice(0, visibleCount);

  if (isLoading && orderCount === 0) {
    return (
      <Stack spacing={1.5} sx={{ px: 2, pt: 1 }}>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={112} />
        ))}
      </Stack>
    );
  }

  if (rows.length === 0) {
    // "No orders" and "all your orders are filtered out" are different situations;
    // the second needs a way out, since the role's default status filter can hide
    // everything without the user having touched a thing.
    const { globalFilter, columnFilters } = table.getState();
    const isFiltered = Boolean(globalFilter) || columnFilters.length > 0;

    return (
      <Box textAlign="center" sx={{ p: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {orderCount > 0 && isFiltered
            ? `None of your ${orderCount} ${orderCount === 1 ? "order matches" : "orders match"} the current filters.`
            : "No orders."}
        </Typography>
        {orderCount > 0 && isFiltered && (
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
            onClick={() => {
              // true = clear to empty, not back to the role defaults that hid them.
              table.resetColumnFilters(true);
              table.setGlobalFilter(undefined);
            }}
          >
            Clear filters
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {visibleRows.map((row) => {
        const order = row.original;
        const statusColor = getStatusColor(order.status).color;
        // Template literals print null as "null"; sparse rows must not.
        const orderLabel = order.number
          ? `${order.prefix ? order.prefix + "-" : ""}${order.number}`
          : "No order number";
        const people = [
          order.heldByFullName && `Closer: ${order.heldByFullName}`,
          order.funder && `Funder: ${order.funder}`
        ].filter(Boolean);

        return (
          <Box key={row.id} sx={{ px: 2, py: 1.75, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Link
                href={order.number ? `${momentumUrl}new-home/${order.number}` : undefined}
                target="_blank"
                underline="none"
                sx={{ fontWeight: 700, fontSize: 15, color: "primary.dark", minWidth: 0 }}
              >
                {orderLabel}
              </Link>
              {order.status && (
                <Chip
                  variant="outlined"
                  size="small"
                  label={order.status}
                  sx={{
                    flexShrink: 0,
                    height: 22,
                    borderRadius: "6px",
                    fontWeight: 600,
                    borderColor: statusColor,
                    color: statusColor
                  }}
                />
              )}
            </Stack>

            {order.projectName && (
              <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                {order.projectName}
              </Typography>
            )}
            {order.propertyAddress && (
              <Typography variant="body2" color="text.secondary">
                {order.propertyAddress}
              </Typography>
            )}
            {order.clientName && (
              // Same cue as the desktop column: clients with few orders flagged.
              <Typography
                variant="body2"
                sx={{ mt: 0.5, fontWeight: 600, color: order.clientOrderCount <= 3 ? "warning.main" : "text.primary" }}
              >
                {order.clientName}
              </Typography>
            )}

            <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={1} sx={{ mt: 0.75 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: "block", fontSize: 13, color: "text.secondary" }}>
                  {[
                    order.settlementDate
                      ? `Closing ${fMonthDayYearSlashNotation(order.settlementDate)}`
                      : "No closing date",
                    order.transactionType === "Purchase" && order.salesPrice
                      ? `P ${fCurrency(order.salesPrice)}`
                      : null,
                    order.loanAmount ? `L ${fCurrency(order.loanAmount)}` : null
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
                {people.length > 0 && (
                  <Typography variant="caption" sx={{ display: "block", fontSize: 13, color: "text.disabled" }}>
                    {people.join(" · ")}
                  </Typography>
                )}
              </Box>

              <Stack direction="row" spacing={1} sx={{ flexShrink: 0, display: order.number ? "flex" : "none" }}>
                <IconButton
                  size="small"
                  aria-label="Closing checklist"
                  href={`/task-management/closingchecklist?order=${order.number}`}
                  target="_blank"
                  sx={{ width: 40, height: 40, bgcolor: "grey.200", "& svg": { width: 20, height: 20 } }}
                >
                  <ChecklistIcon />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Open closing in calendar"
                  href={`/calendar?ordernumber=${order.number}&mode=edit`}
                  target="_blank"
                  sx={{ width: 40, height: 40, bgcolor: "grey.200", "& svg": { width: 20, height: 20 } }}
                >
                  <Iconify icon="eva:calendar-outline" width={20} />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        );
      })}

      {visibleCount < rows.length && (
        <Box sx={{ px: 2, py: 2 }}>
          <Button fullWidth variant="outlined" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
            Show more ({rows.length - visibleCount} left)
          </Button>
        </Box>
      )}
    </Box>
  );
}
